import { Controller, type Control, type FieldErrors } from 'react-hook-form';

import CheckIcon from '@common/assets/icons/check.svg?react';
import { CachedImage } from '@common/ui/CachedImage';
import { Card } from '@common/ui/Card';
import { Heading } from '@common/ui/typography/Heading';
import { Paragraph } from '@common/ui/typography/Paragraph';

import { analytics, type SignupRole } from '@services/AnalyticsService';

import styles from './Role.module.scss';

import type { SignUpFormData } from '../../SignUp';

const LIKE_ICON = '/icons/3d-icons/like.png';
const MESSAGE_ICON = '/icons/3d-icons/message.png';

const ROLE_OPTIONS: { value: SignupRole; title: string; hint: string }[] = [
  {
    value: 'dating',
    title: 'Знакомиться',
    hint: 'Искать пару для себя',
  },
  {
    value: 'matchmaker',
    title: 'Сватать друзей',
    hint: 'Листать анкеты и советовать их друзьям. Нужны только имя, возраст, город и одно фото',
  },
  {
    value: 'both',
    title: 'И то, и другое',
    hint: 'Знакомиться самому и помогать друзьям',
  },
];

const RoleIcon = ({ role }: { role: SignupRole }) => {
  if (role === 'both') {
    return (
      <span className={styles.icon} data-tone={role}>
        <CachedImage
          testId="role-option-icon"
          className={styles.pairFirst}
          src={LIKE_ICON}
          alt=""
          blur="2px"
        />
        <CachedImage
          testId="role-option-icon"
          className={styles.pairSecond}
          src={MESSAGE_ICON}
          alt=""
          blur="2px"
        />
      </span>
    );
  }

  return (
    <span className={styles.icon} data-tone={role}>
      <CachedImage
        testId="role-option-icon"
        className={styles.single}
        src={role === 'dating' ? LIKE_ICON : MESSAGE_ICON}
        alt=""
        blur="2px"
      />
    </span>
  );
};

interface RoleProps {
  control: Control<SignUpFormData>;
  errors: FieldErrors<SignUpFormData>;
}

/** The sign-up's first step with the matchmaker feature: how Kiss is used. */
export const Role = ({ control, errors }: RoleProps) => {
  return (
    <>
      <Heading level={2} align="center">
        Как вы хотите пользоваться Kiss?
      </Heading>
      <Paragraph
        variant="secondary"
        align="center"
        style={{ marginTop: -20, paddingBottom: 8 }}
      >
        Можно поменять потом в настройках
      </Paragraph>

      <Controller
        control={control}
        name="role"
        render={({ field }) => (
          <div className={styles.options} role="radiogroup">
            {ROLE_OPTIONS.map((option) => {
              const isSelected = field.value === option.value;

              return (
                <Card
                  key={option.value}
                  testId={`role-option-${option.value}`}
                  className={styles.option}
                  variant="outlined"
                  selected={isSelected}
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => {
                    analytics.trackSignUpStep('role_tapped', {
                      role: option.value,
                    });
                    field.onChange(option.value);
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 12,
                    padding: 16,
                  }}
                >
                  <RoleIcon role={option.value} />
                  <span className={styles.text}>
                    <span className={styles.title}>{option.title}</span>
                    <span className={styles.hint}>{option.hint}</span>
                  </span>
                  <span className={styles.check}>
                    {isSelected && <CheckIcon />}
                  </span>
                </Card>
              );
            })}
          </div>
        )}
      />

      {errors.role?.message && (
        <span className={styles.error} data-test-id="role-error">
          {errors.role.message}
        </span>
      )}
    </>
  );
};
